import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ICON_COLOR } from '@zep-us/zus';
import { BoltFillColorMintIcon, BoltFillColorVioletIcon, ExportIcon, GearIcon, HouseIcon, LogoutIcon, PenIcon, PersonStackIcon, SchoolIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { BoltFillIcon, BoltIcon, BoltSmallRectangleColorVioletIcon, BookCoverIcon, CheckSmallIcon, ChevronDownIcon, GlobeIcon, SquareGridIcon, XIcon } from '@/icons';
import { CreateButton } from '@/layout/Header/CreateButton';
import { useIsMobileHeaderViewport } from '@/layout/Header/hooks/useIsMobileHeaderViewport';
import { useLockBodyScroll } from '@/layout/Header/hooks/useLockBodyScroll';
import { ProfileIconButton } from '@/layout/Header/MobileDrawer/ProfileIconButton';
import { EnterCodeButton } from '@/layout/Header/PageNavigatorButton';
import { useUser } from '@/lib/auth';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { MyMenuDropDownLanguageSelector } from '@/shared/components/LanguageSelector';
import { useLanguageChange } from '@/shared/components/LanguageSelector/hooks/useLanguageChange';
import { useLanguageOptions } from '@/shared/components/LanguageSelector/hooks/useLanguageOptions';
import { CLASSROOM_ROUTES } from '@/shared/consts/routes';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { usePremiumAccess } from '@/shared/plan';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/ui';
import { formatLocalizedDate } from '@/utils/dateFormat';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
import { cn } from '@/utils/tailwind.util';
import { trackGnbAnalytics } from './trackGnbAnalytics';
interface MyMenuDropdownProps {
  className?: string;
  /** Outer avatar size forwarded to ProfileIconButton (default 40). */
  triggerSize?: number;
  /** Hide the plan badge overlay on the avatar trigger (default false). */
  hideBadge?: boolean;
  /** Use the full-width mobile product menu layout. */
  mobileMenu?: boolean;
  /** Show the quiz admin entry in profile menu. Intended for the main product header only. */
  showAdminMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}
export const MyMenuDropdown = ({
  className,
  triggerSize,
  hideBadge,
  mobileMenu = false,
  showAdminMenu = false,
  onOpenChange
}: MyMenuDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    user,
    logOut
  } = useUser();
  const isMobileHeaderViewport = useIsMobileHeaderViewport();
  const isInExternalPlatform = useIsInExternalPlatform();
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    openPlanGuide
  } = usePremiumAccess();
  const isWorkspaceOwner = user?.workspace?.isPaidOwner ?? false;
  const isPublicHeaderContext = router.pathname.startsWith('/public');
  // 학생은 플랜을 구매하는 주체가 아니다(결제는 선생님·워크스페이스 소유자 몫).
  const shouldShowPlanUpgrade = !isInExternalPlatform && !!user && user.type?.toLowerCase() !== 'student' && user.planType !== 'PRO';
  const shouldShowAdminMenu = showAdminMenu && !!user?.admin;
  const formatLabel = (label: string) => formatEnglishTitleCase(label, i18n.resolvedLanguage);
  const zepQuizIntroLabel = t('common.header.aboutZepQuiz');
  useEffect(() => {
    if (!mobileMenu) return;
    onOpenChange?.(isOpen);
  }, [isOpen, mobileMenu, onOpenChange]);
  useEffect(() => {
    if (!mobileMenu || isMobileHeaderViewport || !isOpen) return;
    setIsOpen(false);
  }, [isMobileHeaderViewport, isOpen, mobileMenu]);
  useLockBodyScroll(mobileMenu && isOpen);
  const handleProfileClick = () => {
    trackGnbAnalytics('mylist', 'Header');
    void router.push('/profile/edit');
    setIsOpen(false);
  };
  const handlePlanClick = () => {
    // enter_voucher(이용권 진입) 지표에 플랜 관리 클릭이 섞이지 않도록 분리한 이벤트.
    trackGnbAnalytics('plan_manage', 'Header');
    void router.push('/profile/plan');
    setIsOpen(false);
  };
  const handlePlanMemberClick = () => {
    trackGnbAnalytics('plan', 'Header');
    void router.push('/profile/plan-members');
    setIsOpen(false);
  };
  const handleAdminClick = () => {
    window.open('/admin', '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };
  const handleMyQuizClick = async () => {
    await router.push('/myquiz');
    setIsOpen(false);
  };
  const handleMyBoardClick = async () => {
    await router.push('/my-board');
    setIsOpen(false);
  };
  const handleMyClassroomsClick = async () => {
    await router.push(CLASSROOM_ROUTES.list());
    setIsOpen(false);
  };
  const handleHomepageClick = () => {
    window.open('/landing', '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };
  const handleLogoutClick = () => {
    setIsOpen(false);
    void logOut({
      redirectTo: '/landing'
    });
  };
  if (mobileMenu) {
    return <div className={cn('relative', className)}>
        <button type="button" aria-label={isOpen ? String(t('common:common.confirmDialog.cancelText')) : String(t('common:detailProfile'))} aria-expanded={isOpen} aria-haspopup="menu" className="flex items-center justify-center rounded-sm focus:outline-none" onClick={() => setIsOpen(prev => !prev)}>
          {isOpen ? <span className="flex items-center justify-center" style={{
          width: `${triggerSize ?? 40}px`,
          height: `${triggerSize ?? 40}px`
        }}>
              <XIcon size="md" fill={ICON_COLOR.neutral} />
            </span> : <ProfileIconButton size={triggerSize} hideBadge={hideBadge} />}
        </button>
        {isOpen && typeof document !== 'undefined' ? createPortal(<>
                <div role="presentation" className="fixed inset-x-0 bottom-0 top-[50px] z-[100000000] overscroll-none bg-dimmed tablet-sm:hidden" onClick={() => setIsOpen(false)} onWheel={event => event.preventDefault()} onTouchMove={event => event.preventDefault()} />
                <div className="fixed inset-x-0 top-[50px] z-[100000001] overscroll-none bg-white tablet-sm:hidden">
                  <div className="flex max-h-[calc(100dvh-50px)] w-full flex-col overscroll-none">
                    <div className="min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
                      <div className="flex flex-col gap-xs px-md pb-md pt-xs">
                        {shouldShowPlanUpgrade && <MobilePlanUpgradeMenuItem onClick={() => {
                  void openPlanGuide({
                    highlightPlan: 'PRO',
                    from: isPublicHeaderContext ? 'public_header_profile_menu' : 'product_header_profile_menu'
                  });
                  setIsOpen(false);
                }}>
                            {t('common:planUpgrade')}
                          </MobilePlanUpgradeMenuItem>}
                        <MobileProfileMenuItem onClick={handleProfileClick}>
                          {formatLabel(t('common:detailProfile'))}
                        </MobileProfileMenuItem>
                        <MobileProfileMenuItem onClick={handlePlanClick}>
                          {formatLabel(t('common:planManage'))}
                        </MobileProfileMenuItem>
                        {isWorkspaceOwner && <MobileProfileMenuItem onClick={handlePlanMemberClick}>
                            {formatLabel(t('common.plan.memberManage'))}
                          </MobileProfileMenuItem>}
                        {shouldShowAdminMenu && <MobileProfileMenuItem leftSlot={<GearIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />} onClick={handleAdminClick}>
                            {t('common:admin')}
                          </MobileProfileMenuItem>}
                      </div>
                      <div className="h-px w-full bg-alternative" />
                      <div className="flex flex-col gap-xs px-md py-[12px]">
                        <MobileProfileMenuItem onClick={handleMyQuizClick}>
                          {formatLabel(t('common:myQuiz'))}
                        </MobileProfileMenuItem>
                        <MobileProfileMenuItem onClick={handleMyBoardClick}>
                          <span>{formatLabel(t('common:myBoard'))}</span>
                        </MobileProfileMenuItem>
                        <MobileProfileMenuItem onClick={handleMyClassroomsClick}>
                          <span>
                            {formatLabel(t('common:classroom.myClassrooms'))}
                          </span>
                          <ProfileMenuBetaChip />
                        </MobileProfileMenuItem>
                      </div>
                      <div className="h-px w-full bg-alternative" />
                      <MobileProfileLanguageSelector />
                      <div className="h-px w-full bg-alternative" />
                      <div className="flex flex-col gap-xs px-md py-[12px]">
                        <MobileProfileMenuItem leftSlot={<HouseIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />} rightSlot={<ExportIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />} onClick={handleHomepageClick}>
                          {zepQuizIntroLabel}
                        </MobileProfileMenuItem>
                      </div>
                      <div className="h-px w-full bg-alternative" />
                      <div className="flex flex-col gap-xs px-md py-[12px]">
                        <MobileProfileMenuItem leftSlot={<LogoutIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" />} onClick={handleLogoutClick}>
                          {t('common:header.logout')}
                        </MobileProfileMenuItem>
                      </div>
                    </div>
                    <div className="h-px w-full shrink-0 bg-alternative" />
                    <div className="flex shrink-0 flex-col gap-xs px-md pb-md pt-[12px]">
                      <CreateButton compact hideRightSlot className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" />
                      <EnterCodeButton type="normal" compact className="h-[40px] min-h-[40px] w-full rounded-sm px-md text-body-md-semibold" textClassName="truncate" />
                    </div>
                  </div>
                </div>
              </>, document.body) : null}
      </div>;
  }
  if (!user) return null;
  return <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false} data-sentry-element="DropdownMenu" data-sentry-component="MyMenuDropdown" data-sentry-source-file="MyMenuDropdown.tsx">
      <DropdownMenuTrigger className={cn('flex items-center rounded-sm focus:outline-none', className)} tabIndex={-1} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="MyMenuDropdown.tsx">
        <ProfileIconButton size={triggerSize} hideBadge={hideBadge} data-sentry-element="ProfileIconButton" data-sentry-source-file="MyMenuDropdown.tsx" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[800px] w-[180px] min-w-[180px] rounded-sm border border-assistive px-0 py-3xs shadow-lg" data-sentry-element="DropdownMenuContent" data-sentry-source-file="MyMenuDropdown.tsx">
        <DropdownMenuGroup data-sentry-element="DropdownMenuGroup" data-sentry-source-file="MyMenuDropdown.tsx">
          <DropdownMenuSection data-sentry-element="DropdownMenuSection" data-sentry-source-file="MyMenuDropdown.tsx">
            {!isInExternalPlatform && <MyMenuDropdownItem key="plan" className={user?.planType === 'FREE' ? 'cursor-pointer' : 'cursor-default'} text={<PlanStatus />} icon={user?.planType === 'BASIC' ? <BoltFillColorMintIcon size="xs" /> : user?.planType === 'FREE' ? <span className="flex size-[12px] items-center justify-center rounded-[2px] bg-gray-300">
                      <BoltFillIcon width={9} height={9} fill={ICON_COLOR.inverse} />
                    </span> : <BoltFillColorVioletIcon size="xs" />} />}
            <MyMenuDropdownItem key="profile" text={formatLabel(t('common:detailProfile'))} icon={<PenIcon size="xs" fill={ICON_COLOR.neutral} />} onClick={handleProfileClick} data-sentry-element="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />
            <MyMenuDropdownItem key="plan-manage" text={formatLabel(t('common:planManage'))} icon={<BoltIcon size="xs" fill={ICON_COLOR.neutral} />} onClick={handlePlanClick} data-sentry-element="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />
            {isWorkspaceOwner && <PlanMemberMenuDropdownItem onClose={() => setIsOpen(false)} />}
            {shouldShowAdminMenu && <MyMenuDropdownItem key="admin" text={t('common:admin')} icon={<GearIcon size="xs" fill={ICON_COLOR.default} />} onClick={handleAdminClick} />}
          </DropdownMenuSection>
          <DropdownMenuSeparator className="!mx-0 !my-0 h-px border-0 border-t border-alternative !bg-transparent" data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="MyMenuDropdown.tsx" />
          <DropdownMenuSection data-sentry-element="DropdownMenuSection" data-sentry-source-file="MyMenuDropdown.tsx">
            <MyMenuDropdownItem key="my-quiz" text={formatLabel(t('common:myQuiz'))} icon={<BookCoverIcon size="xs" fill={ICON_COLOR.neutral} />} rightSlot={router.pathname.startsWith('/myquiz') ? <CheckSmallIcon size="xs" fill={ICON_COLOR.neutral} /> : null} onClick={handleMyQuizClick} data-sentry-element="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />
            <MyMenuDropdownItem key="my-board" text={formatLabel(t('common:myBoard'))} icon={<SquareGridIcon size="xs" fill={ICON_COLOR.neutral} />} rightSlot={router.pathname.startsWith('/my-board') ? <CheckSmallIcon size="xs" fill={ICON_COLOR.neutral} /> : null} onClick={handleMyBoardClick} data-sentry-element="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />
            <MyMenuDropdownItem key="my-classrooms" text={formatLabel(t('common:classroom.myClassrooms'))} icon={<SchoolIcon size="xs" fill={ICON_COLOR.neutral} />} rightSlot={router.pathname.startsWith('/classrooms') ? <CheckSmallIcon size="xs" fill={ICON_COLOR.neutral} /> : null} onClick={handleMyClassroomsClick} data-sentry-element="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />
          </DropdownMenuSection>
          <DropdownMenuSeparator className="!mx-0 !my-0 h-px border-0 border-t border-alternative !bg-transparent" data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="MyMenuDropdown.tsx" />
          <DropdownMenuSection data-sentry-element="DropdownMenuSection" data-sentry-source-file="MyMenuDropdown.tsx">
            <MyMenuDropDownLanguageSelector data-sentry-element="MyMenuDropDownLanguageSelector" data-sentry-source-file="MyMenuDropdown.tsx" />
          </DropdownMenuSection>
          <LogoutButtonForAdmin data-sentry-element="LogoutButtonForAdmin" data-sentry-source-file="MyMenuDropdown.tsx" />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>;
};
const PlanMemberMenuDropdownItem = (props: {
  onClose: () => void;
}) => {
  const router = useRouter();
  const {
    t,
    i18n
  } = useTranslation(['common']);
  return <MyMenuDropdownItem key="plan" text={formatEnglishTitleCase(t('common.plan.memberManage'), i18n.resolvedLanguage)} icon={<PersonStackIcon fill={ICON_COLOR.neutral} size="xs" />} onClick={() => {
    trackGnbAnalytics('plan', 'Header');
    void router.push('/profile/plan-members');
    props.onClose();
  }} data-sentry-element="MyMenuDropdownItem" data-sentry-component="PlanMemberMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx" />;
};
const DropdownMenuSection = ({
  children
}: {
  children: ReactNode;
}) => <div className="flex w-full flex-col px-xs" data-sentry-component="DropdownMenuSection" data-sentry-source-file="MyMenuDropdown.tsx">{children}</div>;
const LogoutButtonForAdmin = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    user,
    logOut
  } = useUser();
  if (!user?.admin) return null;
  return <MyMenuDropdownItem key="logout" className={'text-destructive hover:text-destructive focus:text-destructive'} text={<>{t('common:header.logout')} (ForAdmin)</>} icon={<LogoutIcon size={'xs'} fill={ICON_COLOR.destructive} />} onClick={() => {
    void logOut({
      redirectTo: '/landing'
    });
  }} data-sentry-element="MyMenuDropdownItem" data-sentry-component="LogoutButtonForAdmin" data-sentry-source-file="MyMenuDropdown.tsx" />;
};
function MyMenuDropdownItem({
  text,
  icon,
  rightSlot,
  onClick,
  className
}: {
  text: ReactNode;
  icon: ReactNode;
  rightSlot?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return <DropdownMenuItem onClick={onClick} className={cn('min-h-[36px] rounded-2xs px-2xs py-4xs text-body-md-medium text-default hover:bg-neutral focus:bg-neutral', className)} data-sentry-element="DropdownMenuItem" data-sentry-component="MyMenuDropdownItem" data-sentry-source-file="MyMenuDropdown.tsx">
      <div className="flex min-w-0 flex-1 items-center gap-3xs">
        <span className="flex size-[16px] shrink-0 items-center justify-center">
          {icon}
        </span>
        <span className="min-w-0 flex-1 truncate">{text}</span>
      </div>
      {rightSlot ? <span className="flex size-[16px] shrink-0 items-center justify-center">
          {rightSlot}
        </span> : null}
    </DropdownMenuItem>;
}
const ProfileMenuBetaChip = () => <BetaChip variant="outlined" className="h-[18px] min-h-[18px] shrink-0 border-yellow-600 bg-yellow-50 px-3xs text-caption-md-semibold text-yellow-900" data-sentry-element="BetaChip" data-sentry-component="ProfileMenuBetaChip" data-sentry-source-file="MyMenuDropdown.tsx" />;
function MobileProfileMenuItem({
  children,
  leftSlot,
  rightSlot,
  onClick
}: {
  children: ReactNode;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  onClick?: () => void;
}) {
  return <button type="button" className={cn('flex h-[48px] w-full items-center rounded-sm text-left text-body-lg-semibold text-neutral hover:bg-opacity-black-1', leftSlot ? 'pr-md' : 'pl-[8px] pr-md')} onClick={onClick} data-sentry-component="MobileProfileMenuItem" data-sentry-source-file="MyMenuDropdown.tsx">
      {leftSlot ? <span className="flex size-[40px] shrink-0 items-center justify-center">
          {leftSlot}
        </span> : null}
      <span className="flex min-w-0 flex-1 items-center gap-xs truncate">
        {children}
      </span>
      {rightSlot ? <span className="flex size-[40px] shrink-0 items-center justify-center">
          {rightSlot}
        </span> : null}
    </button>;
}
function MobilePlanUpgradeMenuItem({
  children,
  onClick
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return <button type="button" className="flex h-[48px] w-full items-center rounded-sm pr-md text-left text-body-lg-semibold text-neutral hover:bg-opacity-black-1" onClick={onClick} data-sentry-component="MobilePlanUpgradeMenuItem" data-sentry-source-file="MyMenuDropdown.tsx">
      <span className="flex size-[40px] shrink-0 items-center justify-center">
        <BoltSmallRectangleColorVioletIcon width={24} height={24} className="block size-[24px] shrink-0" aria-hidden="true" data-sentry-element="BoltSmallRectangleColorVioletIcon" data-sentry-source-file="MyMenuDropdown.tsx" />
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>;
}
const MobileProfileLanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const languageOptions = useLanguageOptions();
  const {
    currentLocale,
    handleLanguageChange
  } = useLanguageChange({
    location: 'gnb_dropdown'
  });
  return <div className="px-md py-[12px]" data-sentry-component="MobileProfileLanguageSelector" data-sentry-source-file="MyMenuDropdown.tsx">
      <button type="button" className="flex h-[48px] w-full items-center rounded-sm text-body-lg-semibold text-neutral hover:bg-opacity-black-1" onClick={() => setIsOpen(prev => !prev)}>
        <span className="flex size-[40px] shrink-0 items-center justify-center">
          <GlobeIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="GlobeIcon" data-sentry-source-file="MyMenuDropdown.tsx" />
        </span>
        <span className="min-w-0 flex-1 truncate text-left">
          {currentLocale.toUpperCase()}
        </span>
        <span className="flex size-[40px] shrink-0 items-center justify-center">
          <ChevronDownIcon size="sm" fill={ICON_COLOR.neutral} className={cn('transition-transform duration-200', isOpen && 'rotate-180')} data-sentry-element="ChevronDownIcon" data-sentry-source-file="MyMenuDropdown.tsx" />
        </span>
      </button>
      {isOpen && <div className="pb-xs">
          {languageOptions.map(option => {
        const isSelected = currentLocale === option.value;
        return <button key={option.value} type="button" className={cn('flex h-[40px] w-full items-center rounded-sm px-[40px] text-left text-body-md-semibold hover:bg-opacity-black-1', isSelected ? 'text-strong' : 'text-neutral')} onClick={() => {
          void handleLanguageChange(option.value);
        }}>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </button>;
      })}
        </div>}
    </div>;
};
const PlanStatus = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    user
  } = useUser();
  const router = useRouter();
  const planType = user?.planType;
  if (!user || user?.planType === 'FREE') {
    return <div className={'cursor-pointer'} onClick={() => {
      setReferrer({
        from: router.asPath,
        location: router.pathname.startsWith('/public') ? 'public_header_profile_menu' : 'header_profile_menu'
      });
      void router.push('/pricing');
    }}>
        <span className="text-body-md-medium text-default">
          {t('common:plan.usingFree')}
        </span>
      </div>;
  }
  return <div className={'flex flex-col'} data-sentry-component="PlanStatus" data-sentry-source-file="MyMenuDropdown.tsx">
      <span className={'text-caption-md-semibold text-assistive'}>
        {t('common.header.eventEndDate', {
        endDate: formatLocalizedDate(user.planDuration.endDate)
      })}
      </span>
      <span className={cn('text-body-sm-bold', {
      'text-semi-strong': planType === 'BASIC',
      'text-strong': planType === 'PRO'
    })}>
        {planType} {t('common:profile.plan.apply')}
      </span>
    </div>;
};